<template>
    <div class='container'>
        <div class="content-container">
            <div class="title-container">
                <span>
                    {{ $t('reglements.title') }}
                    <ReglePastille cle="reglementsFournisseur.definition" />
                </span>
            </div>
            <div class="filter-container">
                <FilterFournisseur
                    @search="search"
                    @clearFilter="clearFilter"
                    @setFactureDateFrom="setFactureDateFrom"
                    @setFactureDateTo="setFactureDateTo"
                    @comparison="setComparison"
                    @comparisonValue="setComparisonValue"
                    @addFilterSociete="addSociete"
                    @removeFilterSociete="removeSociete"
                    @multipleSelectSearchSociete="multipleSelectSearchSociete"
                    @societeToggleSelectAll="societeToggleSelectAll"
                    :comparison-list-facture="comparisonListFacture"
                    :filter="filter"
                    :societe-list="societeList"
                    :societe-list-total="societeListTotal"
                />
            </div>
            <div class="dataTable">
                <div class="headerWrapper">
                    <h2>
                        {{ count }}
                        {{ $t('reglement.fournisseur.dataTable.title', count > 1 ? 2 : 1) }}
                    </h2>
                    <BorderedIconButton
                        v-if="tableData.length > 0"
                        class="export-button"
                        @click="exportXls"
                        :imgUrl="iconeXls" :title="$t('factures.export')"/>
                </div>
                <LoaderComponent v-if="tableData.length === 0 && isLoading" class="loader" />
                <div v-else @scroll="handleScroll" ref="content" class="datatable-container">
                    <DataTable
                        :count="+count"
                        :tableData="tableData"
                        :tableHeader="tableHeader"
                        :isLoading="isLoading"
                        :stop-propa-on-pdf="true"
                        @sort="doSort"
                        @select="goToPage"
                    />
                </div>
            </div>
            <WaitingModal v-if="isExportXlsLoading"/>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import FilterFournisseur from '@/modules/Common/components/FilterFournisseur.vue';
import { SocieteDTO } from '@/models/SocieteDTO';
import { SortType, sortValue } from '@/models/SortType';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import router from '@/router';
import DataTable from '@/components/ui-components/DataTable.vue';
import { ReglementFournisseurActions } from '@/modules/ReglementsFournisseurs/store/action-types';
import { REGLEMENT_FOURNISSEUR } from '@/modules/ReglementsFournisseurs/store/mutation-types';
import { ReglementDTO } from '@/models/ReglementDTO';
import { Operateur } from '@/models/FactureFournisseurDTO';
import AccountService from '@/modules/Account/services/AccountService';
import { store } from '@/plugins/store';
import { MENU } from '@/components/layout/store/mutation-types';
import { MENU_FOURNISSEUR, MENU_FOURNISSEUR_SWITCH_CLIENT } from '@/components/layout/store/state';
import dayjs from 'dayjs';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import ReglePastille from '@/components/regles/ReglePastille.vue';

interface Data {
    societeList: SocieteDTO[];
    societeListTotal: SocieteDTO[];
    filter: {
        comparisonFacture: string,
        comparisonValue?: number,
        factureDateFrom: string,
        factureDateTo: string,
        societes: SocieteDTO[],
    };
    comparisonListFacture: string[];
}

export default defineComponent({
    name: 'FacturesFournisseurList',
    components: { WaitingModal, BorderedIconButton, LoaderComponent, DataTable, FilterFournisseur, ReglePastille },
    data(): Data {
        return {
            societeList: [],
            societeListTotal: [],
            filter: {
                comparisonFacture: this.$t('facture.suivi.dropdown.novalue'),
                comparisonValue: undefined,
                factureDateFrom: '',
                factureDateTo: '',
                societes: []
            },
            comparisonListFacture: [
                this.$t('facture.suivi.dropdown.novalue'),
                this.$t('label.fournisseur.dropdown.lessthan'),
                this.$t('label.fournisseur.dropdown.greaterthan'),
            ],
        };
    },
    setup() {
        const content = ref<Element>();

        onMounted(() => {
            content.value;
        });

        return { content };
    },
    async beforeMount() {
        if (localStorage.getItem('isFournisseur') === 'true' && localStorage.getItem('isClient') === 'true') {
            store.commit(`menuState/${MENU.SET_MENU}`, MENU_FOURNISSEUR_SWITCH_CLIENT);
        } else {
            store.commit(`menuState/${MENU.SET_MENU}`, MENU_FOURNISSEUR);
        }
        await this.getReglements();
        AccountService.getSocieteIds().then((societes: any) => {
            this.societeList = societes.data;
        });
    },
    computed: {
        sort(): {
            sortData: string;
            sortStatus: SortType;
        } {
            return this.$store.getters['reglementFournisseurState/getFactureSort'];
        },
        tableData() {
            if (this.$store.getters['reglementFournisseurState/getReglements'] == undefined) {
                return [];
            }
            return this.$store.getters['reglementFournisseurState/getReglements'].map(
                ({id, dateReglement, numero, montantTTC, devise}: ReglementDTO) => {
                    return {
                        idMasque: id,
                        date: dayjs(dateReglement).format('DD/MM/YYYY'),
                        numero: numero,
                        montantTTC: montantTTC + ' ' + this.$t('devise.' + devise),
                    };
                }
            );
        },
        tableHeader() {
            return this.$store.getters['reglementFournisseurState/getHeader'];
        },
        isLoading(): boolean {
            return this.$store.getters['reglementFournisseurState/isLoading'];
        },
        getPage(): number {
            return this.$store.getters['reglementFournisseurState/getReglementPage'];
        },
        getNumberOfPages(): number {
            return this.$store.getters['reglementFournisseurState/getReglementNumberOfPage'];
        },
        count() {
            return this.$store.getters['reglementFournisseurState/getReglementCount'];
        },
        iconeXls() {
            return require('@/assets/img/xls.svg');
        },
        isExportXlsLoading(): boolean {
            return store.getters['reglementFournisseurState/isXlsLoading'];
        },
    },
    methods: {
        async getReglements(page = 0) {
            await this.$store.dispatch(`reglementFournisseurState/${ReglementFournisseurActions.GET_REGLEMENTS}`, {
                page,
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                dateDebut: this.filter.factureDateFrom,
                dateFin: this.filter.factureDateTo,
                societeIds: this.filter.societes.map(societe => societe.id),
                operateur: this.getOperateur(),
                montant: this.filter.comparisonValue
            });
        },
        getOperateur() {
            switch (this.filter.comparisonFacture) {
                case ' - ':
                    return null;
                case 'Inférieur ou égal':
                    return Operateur.inferieur;
                case 'Supérieur ou égal':
                    return Operateur.superieur;
            }
        },
        search() {
            this.$store.commit(`reglementFournisseurState/${REGLEMENT_FOURNISSEUR.RESET_ITEMS}`);
            this.doFilter();
        },
        doFilter() {
            this.$store.commit(`reglementFournisseurState/${REGLEMENT_FOURNISSEUR.SET_FILTER_OPERATEUR}`, this.filter.comparisonFacture);
            this.$store.commit(`reglementFournisseurState/${REGLEMENT_FOURNISSEUR.SET_FILTER_MONTANT}`, this.filter.comparisonValue);
            this.$store.commit(`reglementFournisseurState/${REGLEMENT_FOURNISSEUR.SET_FILTER_DATE_DEBUT}`, this.filter.factureDateFrom);
            this.$store.commit(`reglementFournisseurState/${REGLEMENT_FOURNISSEUR.SET_FILTER_DATE_FIN}`, this.filter.factureDateTo);
            this.getReglements();
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            const { sortData, sortStatus } = sort;
            this.$store.commit(`reglementFournisseurState/${REGLEMENT_FOURNISSEUR.SET_SORT}`, { sortData, sortStatus });
            this.$store.commit(`reglementFournisseurState/${REGLEMENT_FOURNISSEUR.RESET_ITEMS}`);
            this.getReglements();
        },
        setFactureDateFrom(value: string): void {
            this.filter.factureDateFrom = value;
        },
        setFactureDateTo(value: string): void {
            this.filter.factureDateTo = value;
        },
        setComparison(comparison: string) {
            this.filter.comparisonFacture = comparison;
        },
        setComparisonValue(comparisonValue: number) {
            this.filter.comparisonValue = comparisonValue;
        },
        clearFilter() {
            this.filter.comparisonFacture = '';
            this.filter.comparisonValue = undefined;
            this.filter.factureDateFrom = '';
            this.filter.factureDateTo = '';

            this.doFilter();
        },
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            this.$store.commit(`reglementFournisseurState/${REGLEMENT_FOURNISSEUR.SET_SCROLL}`, top);
            if (max - top <= height * 1.5 && !this.isLoading && this.getPage + 1 < this.getNumberOfPages) {
                this.getReglements(this.getPage + 1);
            }
        },
        goToPage(item: { idMasque: number; }){
            router.push({ name: 'reglementFournisseurPage', params: { id: item.idMasque } });
        },
        multipleSelectSearchSociete(search: string) {
            const societeFiltre = this.societeList?.filter(societe=> societe.libelle.toLowerCase().includes(search.toLowerCase()));
            this.filter.societes = societeFiltre === undefined ? [] : societeFiltre;
        },
        addSociete(societe: SocieteDTO) {
            this.filter.societes.push(societe);
        },
        removeSociete(societe: SocieteDTO) {
            this.filter.societes = this.filter.societes.filter(itemSociete => itemSociete !== societe);
        },
        societeToggleSelectAll() {
            this.filter.societes = this.filter.societes.length === this.societeList.length ? [] : this.societeList;
        },
        exportXls() {
            this.$store.dispatch(`reglementFournisseurState/${ReglementFournisseurActions.GET_XLS}`, {
                sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                dateDebut: this.filter.factureDateFrom,
                dateFin: this.filter.factureDateTo,
                societeIds: this.filter.societes.length === this.societeList?.length ? [] : this.filter.societes.map(societe => societe.id),
                montant: this.filter.comparisonValue,
                operateur: this.getOperateur(),
            });

        }
    }
});
</script>

<style scoped lang='scss'>
@import '@/theme/pages-recherche.scss';
.margin {
    margin: 10px 10px 10px 20px;
}
.filter-container {
    display: flex;
    padding: 3px 16px;
    background: white;
    border-radius: 6px;
    margin-bottom: 6px;
}
.datatable-container {
    max-height: calc(100vh - 320px);
}
@media screen and (max-width: 1631px) {
    .datatable-container {
        max-height: calc(100vh - 360px);
    }
}

@media screen and (max-width: 1531px) {
    .datatable-container {
        max-height: calc(100vh - 460px);
    }
}

@media screen and (max-width: 970px) {
    .datatable-container {
        max-height: calc(100vh - 518px);
    }
}
.filter {
    width: 100%;
    margin-bottom: 0;
    padding: 0 16px;
}
.container {
    padding-top: 8px;
}
.title-container {
    margin-bottom: 8px;
}
.filter-container {
    padding-top: 2px;
}
:deep(.filterAndButtons) {
    margin-bottom: 0;
}
.dataTable {
    padding-top: 0;
}
</style>
